import { Button, ButtonProps } from "@material-ui/core";
import { FunctionComponent } from "react";
import { SocialNetwork } from "../../service/data/SocialNetwork";
import { useTranslate } from "../../service/translation";

interface ViewPostButtonProps extends ButtonProps<"a"> {
    /** The network the post lives on. Names it in the label: "View on Bluesky", "View on X". */
    network: SocialNetwork;
}

/**
 * Link out to a post on the network it was published on.
 *
 * Pass the post URL as `href` - on a SocialItem that is `record.sourceUrl`, which despite its name
 * holds the post permalink: apiProvider maps the API's `shareUrl` onto it. The API's own `sourceUrl`
 * is the author's profile, and is not what this button wants.
 *
 * The network's display name comes from `social.network.*`, not from the network id: the id is
 * `twitter` in the code and in the API, but a reader expects "X".
 *
 * Typed as ButtonProps<"a">, not ButtonProps. The default is ButtonProps<"button">, which has no
 * href, target or rel - Button renders an anchor at runtime as soon as it is given an href, but the
 * types only follow if the element is named.
 */
const ViewPostButton: FunctionComponent<ViewPostButtonProps> = (props: ViewPostButtonProps) => {
    const translate = useTranslate();
    const { network, ...buttonProps } = props;
    const label = translate("newshub.action.viewPost", {
        network: translate("social.network." + network),
    });

    // No button variant by default, so a text button is used
    // See https://material-ui.com/components/buttons/#text-buttons
    return (
        <Button
            component="a"
            aria-label={label}
            color="primary"
            size="small"
            {...buttonProps}
        >
            {label}
        </Button>
    );
};

export default ViewPostButton;
